import "./Chatwithelliot.scss";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  SvgIcon,
  Typography,
} from "@mui/material";
import { FunctionComponent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ReactComponent as Send } from "../../Assets/Images/sendicon.svg";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { Tooltip } from "@mui/material";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import {
  chatbotPublic,
  clearChatbotPublicStatus,
} from "../../Redux/Slices/ChatbotPublic/ChatbotPublicSlicer";
import {
  ChatbotPublicProps,
  IChatbotPublic,
} from "../../Components/ChatbotPublic/IChatbotPublic";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import IconButtonField from "../../Components/Button/IconButtonField";
import AvatarImage from "../../Assets/Images/Chatbot_Avatar.png";
import { useNavigate } from "react-router-dom";
import sendImage from "../../Assets/Images/Send_icon.svg";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import CheckIcon from "@mui/icons-material/Check"; // Optional for a tick
import { ChatWithElliot, clearChatWithElliotStatus } from "../../Redux/Slices/ChatwithElliot/ChatwithElliot";
import TypewriterMarkdown from "../../Components/TypewriterMarkdown/TypewriterMarkdown";


const RotatingTextLoader = () => {
  const loadingTexts = [
    { text: "Thinking", emoji: "🤖..." },
    { text: "Analyzing", emoji: "🧠..." },
    { text: "Processing", emoji: "⚙️..." },
    { text: "Computing", emoji: "💻..." },
    { text: "Evaluating", emoji: "📊..." },
    { text: "Generating", emoji: "✨..." },
    { text: "Optimizing", emoji: "🚀..." },
    { text: "Learning", emoji: "📚..." },
    { text: "Reasoning", emoji: "🔎..." },
    { text: "Synthesizing", emoji: "🔗..." },
    { text: "Formulating", emoji: "💡..." },
    { text: "Calculating", emoji: "➗..." },
    { text: "Predicting", emoji: "📈..." },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(Math.floor(Math.random() * loadingTexts.length));
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % loadingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentItem = loadingTexts[currentIndex];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
      <Box sx={{ display: "flex", gap: "4px" }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#3b82f6",
              animation: "bounce 1.4s infinite ease-in-out both",
              animationDelay: `${i * 0.16}s`,
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontStyle: "italic",
            fontWeight: 600,
            background: "linear-gradient(90deg, #4b5563 0%, #9ca3af 50%, #4b5563 100%)",
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "shimmer 2s infinite linear",
          }}
        >
          {currentItem.text}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontStyle: "italic",
            fontWeight: 600,
            color: "#4b5563",
          }}
        >
          {currentItem.emoji}
        </Typography>
      </Box>
      <style>
        {`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}
      </style>
    </Box>
  );
};

const ChatbotElliot: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { data: chatbotData, isLoading: chatloading } = useAppSelector(
    (state) => state.ChatWithElliotSlicer
  );
  const navigate = useNavigate();


  const applicationName =
    window.location.hostname.toLowerCase().includes("ucat") ||
      window.location.hostname.toLowerCase().includes("ucattest") ||
      window.location.hostname.split(".")[0]?.includes("192")
      ? "ucat"
      : "gate";


  const applicationDisplayName = applicationName === "ucat" ? "UCAT" : "GATE";

  const divRef = useRef<HTMLDivElement>(null);

  const methods = useForm<IChatbotPublic>();
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [feedbacks, setFeedbacks] = useState<{
    [key: string]: "helpful" | "not_helpful" | null;
  }>({});
  const [chatHistory, setChatHistory] = useState<
    { sender: "user" | "bot"; message: string; alreadyTyped?: boolean }[]
  >(() => {
    const saved = localStorage.getItem("chat_with_elliot_history");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed)
          ? parsed.map((item: any) => ({ ...item, alreadyTyped: true }))
          : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [isTyping, setIsTyping] = useState(false);

  const isFirstRender = useRef(true);

  useEffect(() => {
    // Save to localStorage whenever history changes, limit to last 50 messages
    const historyToSave = chatHistory.slice(-50);
    localStorage.setItem("chat_with_elliot_history", JSON.stringify(historyToSave));
  }, [chatHistory]);


  const normalizeMessage = (msg: string) => msg.replace(/<[^>]+>/g, "").trim();

  const handleFinishTyping = useCallback((index: number) => {
    setIsTyping(false);
    setChatHistory((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, alreadyTyped: true } : item
      )
    );
  }, []);

  useLayoutEffect(() => {
    dispatch(clearChatWithElliotStatus())
  }, [])

  const onSubmit: SubmitHandler<IChatbotPublic> = (data: any) => {
    if (chatloading || isTyping) return;
    const userMessage = data?.message;
    if (!userMessage?.trim()) return;

    setChatHistory((prev) => [
      ...prev,
      { sender: "user", message: userMessage },
    ]);

    dispatch(
      ChatWithElliot({
        message: userMessage,
        application: applicationName,
      })
    );

    methods.reset({ message: "" });
  };

  useEffect(() => {
    if (
      chatbotData &&
      (typeof chatbotData === "string" || chatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof chatbotData === "string"
          ? chatbotData
          : chatbotData.message || "";

      setChatHistory((prev) => {
        const lastMsg = prev[prev.length - 1];
        if (
          lastMsg?.sender === "bot" &&
          normalizeMessage(lastMsg.message) === normalizeMessage(botMessage)
        ) {
          return prev;
        }
        return [
          ...prev,
          {
            sender: "bot",
            message: botMessage,
            alreadyTyped: isFirstRender.current,
          },
        ];
      });
      if (!isFirstRender.current) {
        setIsTyping(true);
      }
    }
  }, [chatbotData]);


  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollTop = divRef.current.scrollHeight;
    }
  }, [chatHistory]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [chatloading]);


  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return (
    <Box
      sx={{
        mx: "auto",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Message Area */}
      <div
        ref={divRef}
        className="chatbot_message_area"
        style={{
          overflowY: "auto",
          background: "#fff",
          paddingBottom: "16px"
        }}
      >
        {/* Header */}
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            px: 2,
            background: "#f5fbff",
            borderBottom: "1px solid #e0e0e0",
            position: "sticky",
            top: "0",
            zIndex: "1000",
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              background: "#009DFF",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
            }}
          >
            <Avatar
              src={AvatarImage}
              alt="Profile"
              className="blinking-avatar1background"
              sx={{
                width: 40,
                height: 40,
                background: "transparent",
              }}
            />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
            Hey, I'm Elliot, your AI Virtual Assistant
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "16px", mt: 1, color: "#555" }}
          >
            You can ask me anything related to your {applicationDisplayName} exam preparation.
          </Typography>
          <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1 }}>
            <Tooltip title="Clear Chat">
              <IconButtonField
                onClick={() => {
                  setChatHistory([]);
                  localStorage.removeItem("chat_with_elliot_history");
                }}
              >
                <DeleteSweepIcon fontSize="small" />
              </IconButtonField>
            </Tooltip>
            <IconButtonField
              onClick={() => {
                dispatch(clearChatWithElliotStatus());
                // setChatHistory([]); // Commented out to keep history on close
                navigate("/dashboard"); // Replace with your target route
              }}
            >
              <CloseIcon />
            </IconButtonField>
          </Box>
        </Box>

        <Box sx={{ p: 2, margin: "0% 6%" }}>
          {/* Intro Message */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              mb: 1,
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
              fontSize: "14px",
              maxwidth: "60%",
            }}
          >
            <span
              style={{
                borderRadius: "50%",
                background: "linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)",
                width: 40,
                height: 40,
                justifyContent: "center",
                padding: 4,
              }}
            >
              <Avatar
                src={AvatarImage}
                className="chatbot-avatar"
                alt="Profile"
                sx={{ width: 32, height: 32 }}
              />
            </span>
            Elliot
          </Typography>

          <Box
            sx={{
              backgroundColor: "#f3f4f6",
              borderRadius: "12px",
              p: 2,
              mb: 2,
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
              width: "80%"
            }}
          >
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.6, fontSize: "14px", color: "#333" }}
            >
              Hi! I am Elliot, the {applicationDisplayName} AI virtual assistant. You can ask me
              anything about preparation, university admissions or the {applicationDisplayName}{" "}
              online learning platform.
              <br />
              <br />
              Please note: I am still in training, and I would appreciate any
              feedback that you can provide by selecting the upvote/downvote
              buttons below my responses.
            </Typography>
          </Box>
          {/* Feedback buttons under intro message */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <IconButtonField
              size="small"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor:
                  feedbacks["intro"] === "helpful" ? "#4caf50" : "#fff",
                color: feedbacks["intro"] === "helpful" ? "#fff" : "#333",
                borderRadius: "8px",
                fontWeight: 600,
                px: 3,
                py: 1,
                boxShadow:
                  feedbacks["intro"] === "helpful"
                    ? "0 2px 8px rgba(76,175,80,0.12)"
                    : "none",
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  backgroundColor: "#4caf50",
                  color: "#fff",
                },
              }}
              onClick={() =>
                setFeedbacks((prev) => ({ ...prev, intro: "helpful" }))
              }
            >
              <ThumbUpOffAltIcon fontSize="small" />
              Helpful
              {feedbacks["intro"] === "helpful" && (
                <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />
              )}
            </IconButtonField>
            <IconButtonField
              size="small"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor:
                  feedbacks["intro"] === "not_helpful" ? "#4caf50" : "#fff",
                color: feedbacks["intro"] === "not_helpful" ? "#fff" : "#333",
                borderRadius: "8px",
                fontWeight: 600,
                px: 3,
                py: 1,
                boxShadow:
                  feedbacks["intro"] === "not_helpful"
                    ? "0 2px 8px rgba(76,175,80,0.12)"
                    : "none",
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  backgroundColor: "#4caf50",
                  color: "#fff",
                },
              }}
              onClick={() =>
                setFeedbacks((prev) => ({ ...prev, intro: "not_helpful" }))
              }
            >
              <ThumbDownOffAltIcon fontSize="small" />
              Not helpful
              {feedbacks["intro"] === "not_helpful" && (
                <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />
              )}
            </IconButtonField>
          </Box>
          <Box sx={{ display: "flex", gap: "12px", mt: 2, flexWrap: "wrap" }}>
            {["Show my progress", "Practice recommendations", "Study tips"].map(
              (label, index) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #d0d7de",
                    borderRadius: "7px",
                    padding: "6px 14px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#000",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#cacacaff",
                    },
                  }}
                  onClick={() => {
                    setChatHistory((prev) => [
                      ...prev,
                      { sender: "user", message: label },
                    ]);
                    dispatch(
                      ChatWithElliot({
                        message: label,
                        application: applicationName,
                      })
                    );
                  }}
                >
                  {label}
                </Box>
              )
            )}
          </Box>

          {/* Chat History */}
          {chatHistory.map((entry, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: entry.sender === "user" ? "flex-end" : "flex-start",
              }}
            >
              {entry.sender === "bot" && (
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <span
                    style={{
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)",
                      width: 40,
                      height: 40,
                      justifyContent: "center",
                      padding: 4,
                    }}
                  >
                    <Avatar
                      src={AvatarImage}
                      className="chatbot-avatar"
                      alt="Profile"
                      sx={{ width: 32, height: 32 }}
                    />
                  </span>
                  Elliot
                </Typography>
              )}
              <Box
                sx={{
                  backgroundColor: entry.sender === "user" ? "" : "#f3f4f6",
                  color: entry.sender === "user" ? "#fff" : "#333",
                  borderRadius: "16px",
                  padding: 1.5,
                  maxWidth: "85%",
                  fontSize: "14px",
                }}
              >
                {entry.sender === "bot" ? (
                  <TypewriterMarkdown
                    content={entry.message}
                    isLast={index === chatHistory.length - 1 && !entry.alreadyTyped}
                    onFinish={() => handleFinishTyping(index)}
                    onUpdate={() => {
                      if (divRef.current) {
                        divRef.current.scrollTop = divRef.current.scrollHeight;
                      }
                    }}
                    className="chatWithelliot_bot_message"
                  />
                ) : (
                  <Markdown
                    children={entry.message}
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    className="user_bot_message"
                  />
                )}
              </Box>
              {/* Feedback buttons for each bot response */}
              {
                entry.sender === "bot" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    <IconButtonField
                      size="small"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor:
                          feedbacks[index] === "helpful" ? "#4caf50" : "#fff",
                        color: feedbacks[index] === "helpful" ? "#fff" : "#333",
                        borderRadius: "8px",
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        boxShadow:
                          feedbacks[index] === "helpful"
                            ? "0 2px 8px rgba(76,175,80,0.12)"
                            : "none",
                        transition: "background 0.2s, color 0.2s",
                        "&:hover": {
                          backgroundColor: "#4caf50",
                          color: "#fff",
                        },
                      }}
                      onClick={() =>
                        setFeedbacks((prev) => ({ ...prev, [index]: "helpful" }))
                      }
                    >
                      <ThumbUpOffAltIcon fontSize="small" />
                      Helpful
                      {feedbacks[index] === "helpful" && (
                        <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />
                      )}
                    </IconButtonField>
                    <IconButtonField
                      size="small"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor:
                          feedbacks[index] === "not_helpful" ? "#f44336" : "#fff",
                        color:
                          feedbacks[index] === "not_helpful" ? "#fff" : "#333",
                        borderRadius: "8px",
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        boxShadow:
                          feedbacks[index] === "not_helpful"
                            ? "0 2px 8px rgba(76,175,80,0.12)"
                            : "none",
                        transition: "background 0.2s, color 0.2s",
                        "&:hover": {
                          backgroundColor: "#f44336",
                          color: "#fff",
                        },
                      }}
                      onClick={() =>
                        setFeedbacks((prev) => ({
                          ...prev,
                          [index]: "not_helpful",
                        }))
                      }
                    >
                      <ThumbDownOffAltIcon fontSize="small" />
                      Not helpful
                      {feedbacks[index] === "not_helpful" && (
                        <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />
                      )}
                    </IconButtonField>
                  </Box>
                )
              }
            </Box>
          ))}
          {chatloading && (
            <Box
              sx={{
                mb: 2,
                px: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Avatar
                  sx={{ bgcolor: "#009DFF", width: "24px", height: "24px" }}
                  src={AvatarImage}
                  alt="Elliot"
                />
                Elliot
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#f3f4f6",
                  borderRadius: "16px",
                  padding: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  maxWidth: "80%",
                }}
              >
                <RotatingTextLoader />
              </Box>
            </Box>
          )}
        </Box>
      </div >
      <div style={{ borderBottom: "1px solid #e0e0e0" }}></div>
      {/* Footer Input */}

      <footer
        className="chatbot_footer"
        style={{
          margin: "0 6%",
          padding: "16px 0",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            ref={formRef}
            style={{
              display: "flex",
              width: "100%",
              maxWidth: "100%",
              // marginTop: "25px",
            }}
          >
            <TextInputFieldC
              name="message"
              type="text"
              className="chat_input"
              placeholder="Type any question......"
              size="small"
              fullWidth
              // disabled={chatloading || isTyping}
              inputRef={inputRef}
              InputProps={{
                // startAdornment: chatloading && <ChatDotsLoading />,
                sx: {
                  borderRadius: "20px",
                  border: "1px solid #d0d7de",
                  px: 2,
                  py: 0.5,
                  backgroundColor: "#fff",
                  fontSize: "14px",
                },
              }}
            />
            <IconButtonField
              onClick={() => {
                if (!chatloading && !isTyping) formRef.current?.requestSubmit();
              }}
              // disabled={chatloading || isTyping}
              sx={{
                marginLeft: "8px",
                backgroundColor: chatloading || isTyping ? "#bdbdbd" : "#009dff",
                borderRadius: "8px",
                width: "50px",
                minWidth: "unset",
                pointerEvents: chatloading || isTyping ? "none" : "auto",
                "&:hover": {
                  backgroundColor: chatloading || isTyping ? "#bdbdbd" : "#1666c1",
                },
              }}
            >
              <img
                src={sendImage}
                alt="Send"
                style={{ width: 17, height: 17 }}
              />
            </IconButtonField>
          </form>
        </FormProvider>

        <Typography
          variant="caption"
          sx={{
            color: "#6b7280",
            fontSize: "13px",
            textAlign: "center",
            maxWidth: "600px",
          }}
        >
          Elliot can make mistakes. Check important info.
        </Typography>
      </footer>
    </Box >
  );
};

export default ChatbotElliot;
